import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Pen, RotateCcw, Send, Loader2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Regolamento content
const regulationsContent = {
  it: {
    title: "Regolamento Sardinia Kite School",
    rules: [
      {
        number: 1,
        text: "Per le lezioni sei tenuto ad arrivare 15 minuti prima della lezione per iniziare in orario. Motivo: prepararsi in tempo per la lezione. Quindi può capitare che si finisca 5-15 minuti prima."
      },
      {
        number: 2,
        text: "La lezione inizia quando l'attrezzatura viene data."
      },
      {
        number: 3,
        text: "In caso il team ti confermi la lezione, solitamente chiede un deposito o l'intero importo della lezione. RICORDA che la cancellazione o lo spostamento della lezione deve essere comunicato ENTRO LE ORE 18:00 DEL GIORNO PRIMA. PER NESSUN MOTIVO il deposito o la lezione può essere rimborsato in caso di mancata comunicazione entro questo termine. Questo lo specifichiamo in caso il team ti confermi la lezione: la cancellazione può essere fatta solo dalla scuola, anche poco prima della lezione in caso di mancato vento o altri motivi. In quel caso verrà riaccreditato l'intero importo pagato o programmata un'altra lezione."
      },
      {
        number: 4,
        text: "Le lezioni SEMI-PRIVATE sono con 1 attrezzatura alternata tra i partecipanti. Le lezioni di GRUPPO sono con più attrezzature (ognuno ha la sua). IMPORTANTE: La prima lezione di kitesurf NON è con la tavola ai piedi - si inizia con il controllo dell'aquilone a terra e in acqua (body drag)."
      },
      {
        number: 5,
        text: "L'istruttore viene SCELTO DALLA SCUOLA in base alla disponibilità. Non è garantito che sia sempre lo stesso istruttore. Se desideri cambiare istruttore per la lezione successiva, puoi richiederlo e faremo il possibile per accontentarti."
      }
    ],
    close: "Chiudi"
  },
  en: {
    title: "Sardinia Kite School Regulations",
    rules: [
      {
        number: 1,
        text: "You are required to arrive 15 minutes before the lesson to start on time. Reason: to prepare in time for the lesson. Therefore, the lesson may end 5-15 minutes earlier than scheduled."
      },
      {
        number: 2,
        text: "The lesson starts when the equipment is handed over."
      },
      {
        number: 3,
        text: "If the team confirms your lesson, they usually ask for a deposit or the full lesson amount. REMEMBER that cancellation or rescheduling must be communicated BY 6:00 PM THE DAY BEFORE. FOR NO REASON can the deposit or lesson be refunded if not communicated by this deadline. We specify this in case the team confirms your lesson: cancellation can only be made by the school, even shortly before the lesson in case of no wind or other reasons. In that case, the full amount paid will be refunded or another lesson will be scheduled."
      },
      {
        number: 4,
        text: "SEMI-PRIVATE lessons use 1 equipment alternated between participants. GROUP lessons have multiple equipment (each person has their own). IMPORTANT: The first kitesurf lesson is NOT with the board on your feet - you start with kite control on land and in the water (body drag)."
      },
      {
        number: 5,
        text: "The instructor is CHOSEN BY THE SCHOOL based on availability. It is not guaranteed to always be the same instructor. If you wish to change instructor for your next lesson, you can request it and we will do our best to accommodate you."
      }
    ],
    close: "Close"
  },
  fr: {
    title: "Règlement Sardinia Kite School",
    rules: [
      {
        number: 1,
        text: "Vous devez arriver 15 minutes avant le cours pour commencer à l'heure. Raison: se préparer à temps pour le cours. Il est donc possible que le cours se termine 5 à 15 minutes plus tôt."
      },
      {
        number: 2,
        text: "Le cours commence lorsque l'équipement est remis."
      },
      {
        number: 3,
        text: "Si l'équipe confirme votre cours, elle demande généralement un acompte ou le montant total. RAPPELEZ-VOUS que l'annulation ou le report doit être communiqué AVANT 18H00 LA VEILLE. EN AUCUN CAS l'acompte ou le cours ne peut être remboursé si non communiqué avant ce délai. L'annulation ne peut être faite que par l'école, même peu avant le cours en cas de manque de vent ou autres raisons. Dans ce cas, le montant sera remboursé ou un autre cours sera programmé."
      },
      {
        number: 4,
        text: "Les cours SEMI-PRIVÉS utilisent 1 équipement alterné entre les participants. Les cours de GROUPE ont plusieurs équipements (chacun a le sien). IMPORTANT: Le premier cours de kitesurf n'est PAS avec la planche aux pieds - on commence par le contrôle de l'aile au sol et dans l'eau (body drag)."
      },
      {
        number: 5,
        text: "L'instructeur est CHOISI PAR L'ÉCOLE selon la disponibilité. Il n'est pas garanti que ce soit toujours le même instructeur. Si vous souhaitez changer d'instructeur pour votre prochain cours, vous pouvez le demander et nous ferons de notre mieux pour vous satisfaire."
      }
    ],
    close: "Fermer"
  },
  de: {
    title: "Regeln Sardinia Kite School",
    rules: [
      {
        number: 1,
        text: "Sie müssen 15 Minuten vor der Lektion ankommen, um pünktlich zu beginnen. Grund: sich rechtzeitig auf die Lektion vorzubereiten. Daher kann es vorkommen, dass die Lektion 5-15 Minuten früher endet."
      },
      {
        number: 2,
        text: "Die Lektion beginnt, wenn die Ausrüstung übergeben wird."
      },
      {
        number: 3,
        text: "Wenn das Team Ihre Lektion bestätigt, wird normalerweise eine Anzahlung oder der volle Betrag verlangt. DENKEN SIE DARAN: Stornierung oder Verschiebung muss BIS 18:00 UHR AM VORTAG mitgeteilt werden. AUS KEINEM GRUND kann die Anzahlung oder Lektion erstattet werden, wenn nicht rechtzeitig vor dieser Frist kommuniziert. Die Stornierung kann nur von der Schule vorgenommen werden, auch kurz vor der Lektion bei fehlendem Wind oder anderen Gründen. In diesem Fall wird der Betrag erstattet oder eine andere Lektion geplant."
      },
      {
        number: 4,
        text: "HALB-PRIVATE Lektionen verwenden 1 Ausrüstung, die zwischen den Teilnehmern gewechselt wird. GRUPPEN-Lektionen haben mehrere Ausrüstungen (jeder hat seine eigene). WICHTIG: Die erste Kitesurfstunde ist NICHT mit dem Brett an den Füßen - man beginnt mit der Kitekontrolle am Strand und im Wasser (Body Drag)."
      },
      {
        number: 5,
        text: "Der Lehrer wird VON DER SCHULE GEWÄHLT je nach Verfügbarkeit. Es ist nicht garantiert, dass es immer derselbe Lehrer ist. Wenn Sie für Ihre nächste Stunde einen anderen Lehrer wünschen, können Sie dies anfragen und wir werden unser Bestes tun."
      }
    ],
    close: "Schließen"
  },
  es: {
    title: "Reglamento Sardinia Kite School",
    rules: [
      {
        number: 1,
        text: "Debes llegar 15 minutos antes de la lección para empezar a tiempo. Motivo: prepararse a tiempo para la lección. Por lo tanto, puede ocurrir que la lección termine 5-15 minutos antes."
      },
      {
        number: 2,
        text: "La lección comienza cuando se entrega el equipo."
      },
      {
        number: 3,
        text: "Si el equipo confirma tu lección, generalmente pide un depósito o el importe completo. RECUERDA que la cancelación o reprogramación debe comunicarse ANTES DE LAS 18:00 DEL DÍA ANTERIOR. POR NINGÚN MOTIVO se puede reembolsar el depósito o la lección si no se comunica antes de este plazo. La cancelación solo puede ser hecha por la escuela, incluso poco antes de la lección en caso de falta de viento u otros motivos. En ese caso, se reembolsará el importe o se programará otra lección."
      },
      {
        number: 4,
        text: "Las lecciones SEMI-PRIVADAS usan 1 equipo alternado entre los participantes. Las lecciones de GRUPO tienen varios equipos (cada uno tiene el suyo). IMPORTANTE: La primera lección de kitesurf NO es con la tabla en los pies - se empieza con el control de la cometa en tierra y en el agua (body drag)."
      },
      {
        number: 5,
        text: "El instructor es ELEGIDO POR LA ESCUELA según la disponibilidad. No está garantizado que sea siempre el mismo instructor. Si deseas cambiar de instructor para tu próxima lección, puedes solicitarlo y haremos lo posible para complacerte."
      }
    ],
    close: "Cerrar"
  }
};

// Translations for the form
const formTranslations = {
  it: {
    title: "Modulo di Iscrizione",
    subtitle: "Move Sardinia A.S.D.",
    step1Title: "Dati Personali",
    step2Title: "Tipo di Lezione",
    noDepositNotice: "💳 In questa fase non viene richiesto nessun acconto.",
    step3Title: "Firma il Modulo",
    name: "Nome",
    surname: "Cognome",
    email: "Email",
    phone: "Telefono",
    fiscalCode: "Codice Fiscale / ID",
    birthDate: "Data di Nascita",
    birthPlace: "Luogo di Nascita",
    residenceCity: "Città di Residenza",
    residenceAddress: "Indirizzo",
    activity: "Attività (attrezzatura compresa)",
    numParticipants: "Numero partecipanti",
    countryLabel: "Paese di Origine *",
    activitySelectionWarning: "⚠️ Seleziona attentamente attività e durata: questi dati determinano il prezzo e non potranno essere modificati dopo l'invio.",
    participantAge: "Età",
    participantWeight: "Peso (kg)",
    agePlaceholder: "Es: 25",
    weightPlaceholder: "Es: 70",
    level: "Livello",
    levelBeginner: "Completamente principiante",
    levelExperienced: "Ho già fatto qualcosa",
    levelDetails: "Specifica la tua esperienza...",
    preferredDate: "Data Preferita",
    time: "Orario",
    selectTime: "Scegli",
    duration: "Durata",
    hour1: "1 ora",
    hours2: "2 ore private - €130",
    hours2semi: "2 ore semi-private - €90 a testa",
    hours6: "Corso base 6 ore (2-3 giorni)",
    // Activity translations
    kitesurfPrivate: "🪁 Kitesurf Privato",
    kitesurfSemiPrivate: "🪁 Kitesurf Semi-Privato",
    wingsurfPrivate: "🏄 WIN(G)SURF Privato (facile e divertente)",
    wingsurfGroup: "🏄 WIN(G)SURF Gruppo (facile e divertente)",
    wingfoilPrivate: "🪽 Wingfoil Privato",
    wingfoilGroup: "🪽 Wingfoil Gruppo",
    windsurfPrivate: "🌊 Windsurf Privato (solo lezioni principianti)",
    windsurfSemiPrivate: "🌊 Windsurf Semi-Privato",
    // Duration translations
    duration_2h: "2 ore",
    duration_6h: "Corso base 6 ore",
    duration_1h: "1 ora",
    perPerson: "a testa",
    regIncluded: "iscriz. €10 inclusa",
    // Multi-day stay translations
    stayMultipleDays: "Voglio rimanere più giorni",
    stayDuration: "Quanto tempo rimani?",
    stay2to6days: "2-6 giorni",
    stay1week: "1 settimana",
    stay2weeks: "2+ settimane",
    additionalDates: "Seleziona altre date disponibili",
    accommodationShort: "🏠 Per alloggio, controlla: sardiniakiteschool.net/accomodation/ oppure vieni in spiaggia e chiedi della roulotte o van per stare vicino alla scuola!",
    accommodationLong: "🏡 Puoi prenotare la nostra casa in campagna a 15 min dalla scuola!",
    accommodationPrices: "💰 Prezzi camera privata: €60/giorno (bassa stagione Apr-Mag-Set-Ott) | €100/giorno (alta stagione). Casa intera (max 6 persone): €200/giorno bassa stagione | €400/giorno alta stagione. Min. 7 giorni.",
    accommodationLink: "ℹ️ Info: sardiniakiteschool.net/accomodation/",
    declarationsTitle: "Dichiarazioni Obbligatorie",
    insuranceNotice: "⚠️ ASSICURAZIONE ATTREZZATURA NON COMPRESA. In caso di danno, la riparazione parte da €100. Se volete stipulare l'assicurazione, dovete PAGARE IN ANTICIPO la lezione + assicurazione SAFETY TOOL VDWS direttamente alla scuola: costa €39 per 1 anno.",
    healthDeclaration: "Dichiaro di essere in buone condizioni fisiche e di non presentare controindicazioni alla pratica sportiva non agonistica.",
    regulationsDeclaration: "Dichiaro di conoscere e accettare il regolamento dell'associazione Move Sardinia A.S.D.",
    liabilityDeclaration: "Sollevo l'ASD Move Sardinia da ogni responsabilità per danni a terzi o a me stesso durante la pratica dell'attività.",
    privacyDeclaration: "Acconsento al trattamento dei dati personali ai sensi del D.Lgs 196/2003 e GDPR.",
    optionalTitle: "Consensi Opzionali (non obbligatori)",
    mediaConsent: "📸 Acconsento ad essere ripreso/fotografato e che le immagini possano essere utilizzate sui canali social e web della scuola.",
    newsletterConsent: "📧 Desidero ricevere newsletter e aggiornamenti dalla scuola.",
    membershipFee: "✅ Quota associativa €10 già inclusa nel prezzo",
    notRecommended: "Non consigliato",
    duration_4h: "4 ore - Corso Flash",
    windsurfGroup: "🌊 Windsurf Gruppo (solo lezioni principianti)",
    youthPricing: "Prezzo giovani (7-14 anni): €65/ora lezione privata",
    weightWarning: "Il kitesurf non è consigliato per pesi sotto i 30 kg. Ti consigliamo wingsurf o windsurf!",
    minorReminder: "Per i minorenni: inserire i dati del figlio/a e la firma del genitore.",
    kiteTrainerFree: "Quando la scuola non è impegnata, chiedi il kite trainer per imparare a manovrare il kite GRATIS! Depliant informativo con le basi del kitesurf compreso nel corso.",
    returningStudent: "Ho già fatto lezione con voi - €65/h ogni 2 ore",
    rentalUpsellTitle: "Noleggio Attrezzatura (opzionale)",
    rentalUpsellDesc: "Aggiungi 1 ora di noleggio attrezzatura dopo la lezione a prezzo scontato!",
    rentalUpsellPrice: "€20/attrezzatura anziche' €30",
    rentalUpsellAdd: "Aggiungi noleggio",
    rentalEquipmentCount: "Quante attrezzature vuoi noleggiare?",
    rentalEquipmentSingle: "attrezzatura",
    rentalEquipmentPlural: "attrezzature",
    scheduleWarning: "⚠️ Nota importante: Gli orari e i giorni indicati sono preferenze e potrebbero variare in base alle prenotazioni esistenti e alle condizioni del vento. Ti confermeremo data e orario definitivi.",
    ikoCertificateNote: "🏅 Aggiungi €5 per ricevere il certificato IKO internazionale (riconosciuto in tutto il mondo)",
    signatureInstructions: "Usa il dito o il mouse per firmare nel riquadro sottostante.",
    clearSignature: "Cancella Firma",
    signHere: "Firma qui",
    signatureNote: "Nota: Firmando questo modulo accetti tutte le condizioni indicate e confermi la veridicità dei dati inseriti.",
    back: "Indietro",
    next: "Avanti",
    submit: "Invia Modulo",
    sending: "Invio...",
    successMessage: "Modulo inviato con successo! Ti contatteremo presto 🏄‍♀️",
    errorRequired: "Compila tutti i campi obbligatori",
    errorPhone: "📱 Numero non valido. Aggiungi il prefisso internazionale (es. +39 per Italia, +34 Spagna, +33 Francia, +49 Germania, +44 UK).",
    phoneHint: "Inserisci il numero con prefisso internazionale (es.",
    errorDeclarations: "Devi accettare tutte le dichiarazioni",
    errorSignature: "Firma il modulo per procedere",
    errorSubmit: "Errore nell'invio del modulo. Riprova."
  },
  en: {
    title: "Registration Form",
    subtitle: "Move Sardinia A.S.D.",
    step1Title: "Personal Data",
    step2Title: "Lesson Type",
    noDepositNotice: "💳 No deposit is required at this stage.",
    step3Title: "Sign the Form",
    name: "First Name",
    surname: "Last Name",
    email: "Email",
    phone: "Phone",
    fiscalCode: "Tax Code / ID",
    birthDate: "Date of Birth",
    birthPlace: "Place of Birth",
    residenceCity: "City of Residence",
    residenceAddress: "Address",
    activity: "Activity (equipment included)",
    numParticipants: "Number of participants",
    countryLabel: "Country of Origin *",
    activitySelectionWarning: "⚠️ Carefully select activity and duration: these affect the price and cannot be changed after submission.",
    participantAge: "Age",
    participantWeight: "Weight (kg)",
    agePlaceholder: "E.g.: 25",
    weightPlaceholder: "E.g.: 70",
    level: "Level",
    levelBeginner: "Complete beginner",
    levelExperienced: "I have some experience",
    levelDetails: "Describe your experience...",
    preferredDate: "Preferred Date",
    time: "Time",
    selectTime: "Select",
    duration: "Duration",
    hour1: "1 hour",
    hours2: "2 hours private - €130",
    hours2semi: "2 hours semi-private - €90 each",
    hours6: "Basic course 6 hours (2-3 days)",
    // Activity translations
    kitesurfPrivate: "🪁 Kitesurf Private",
    kitesurfSemiPrivate: "🪁 Kitesurf Semi-Private",
    wingsurfPrivate: "🏄 WIN(G)SURF Private (easy & fun)",
    wingsurfGroup: "🏄 WIN(G)SURF Group (easy & fun)",
    wingfoilPrivate: "🪽 Wingfoil Private",
    wingfoilGroup: "🪽 Wingfoil Group",
    windsurfPrivate: "🌊 Windsurf Private (beginner lessons only)",
    windsurfSemiPrivate: "🌊 Windsurf Semi-Private",
    // Duration translations
    duration_2h: "2 hours",
    duration_6h: "Basic course 6 hours",
    duration_1h: "1 hour",
    perPerson: "each",
    regIncluded: "reg. €10 included",
    // Multi-day stay translations
    stayMultipleDays: "I want to stay multiple days",
    stayDuration: "How long are you staying?",
    stay2to6days: "2-6 days",
    stay1week: "1 week",
    stay2weeks: "2+ weeks",
    additionalDates: "Select other available dates",
    accommodationShort: "🏠 For accommodation, check: sardiniakiteschool.net/accomodation/ or come to the beach and ask about the caravan or van to stay near the school!",
    accommodationLong: "🏡 You can book our countryside house 15 min from the school!",
    accommodationPrices: "💰 Private room: €60/day (low season Apr-May-Sep-Oct) | €100/day (high season). Whole house (max 6 people): €200/day low season | €400/day high season. Min. 7 days.",
    accommodationLink: "ℹ️ Info: sardiniakiteschool.net/accomodation/",
    declarationsTitle: "Mandatory Declarations",
    insuranceNotice: "⚠️ EQUIPMENT INSURANCE NOT INCLUDED. Repairs start from €100 in case of damage. If you want insurance, you must PAY IN ADVANCE the lesson + SAFETY TOOL VDWS insurance directly to the school: it costs €39 for 1 year.",
    healthDeclaration: "I declare that I am in good physical condition and have no contraindications for non-competitive sports practice.",
    regulationsDeclaration: "I declare that I know and accept the regulations of the Move Sardinia A.S.D. association.",
    liabilityDeclaration: "I release ASD Move Sardinia from any liability for damages to third parties or myself during the activity.",
    privacyDeclaration: "I consent to the processing of personal data pursuant to Legislative Decree 196/2003 and GDPR.",
    optionalTitle: "Optional Consents (not mandatory)",
    mediaConsent: "📸 I consent to being filmed/photographed and that images may be used on the school's social media and web channels.",
    newsletterConsent: "📧 I would like to receive newsletters and updates from the school.",
    membershipFee: "✅ Membership fee €10 already included in the price",
    notRecommended: "Not recommended",
    duration_4h: "4 hours - Flash Course",
    windsurfGroup: "🌊 Windsurf Group (beginner lessons only)",
    youthPricing: "Youth pricing (7-14 years): €65/hour private lesson",
    weightWarning: "Kitesurfing is not recommended for weights under 30 kg. We suggest wingsurf or windsurf!",
    minorReminder: "For minors: enter the child's data and parent's signature.",
    kiteTrainerFree: "When the school is not busy, ask for the kite trainer to learn kite handling for FREE! Informative leaflet with kitesurfing basics included in the course.",
    returningStudent: "I already had lessons with you - €65/h every 2 hours",
    rentalUpsellTitle: "Equipment Rental (optional)",
    rentalUpsellDesc: "Add 1 hour of equipment rental after your lesson at a discounted price!",
    rentalUpsellPrice: "€20/equipment instead of €30",
    rentalUpsellAdd: "Add rental",
    rentalEquipmentCount: "How many equipment sets do you want to rent?",
    rentalEquipmentSingle: "equipment",
    rentalEquipmentPlural: "equipment sets",
    scheduleWarning: "⚠️ Important note: The indicated times and days are preferences and may vary based on existing bookings and wind conditions. We will confirm the final date and time.",
    ikoCertificateNote: "🏅 Add €5 to receive the international IKO certificate (recognized worldwide)",
    signatureInstructions: "Use your finger or mouse to sign in the box below.",
    clearSignature: "Clear Signature",
    signHere: "Sign here",
    signatureNote: "Note: By signing this form, you accept all the conditions indicated and confirm the accuracy of the data entered.",
    back: "Back",
    next: "Next",
    submit: "Submit Form",
    sending: "Sending...",
    successMessage: "Form submitted successfully! We will contact you soon 🏄‍♀️",
    errorRequired: "Please fill in all required fields",
    errorDeclarations: "You must accept all declarations",
    errorSignature: "Please sign the form to proceed",
    errorSubmit: "Error submitting the form. Please try again.",
    errorPhone: "📱 Invalid phone number. Add the international prefix (e.g. +39 Italy, +34 Spain, +33 France, +49 Germany, +44 UK).",
    phoneHint: "Enter your phone number with international prefix (e.g."
  },
  fr: {
    title: "Formulaire d'inscription",
    subtitle: "Move Sardinia A.S.D.",
    step1Title: "Données Personnelles",
    step2Title: "Type de Cours",
    step3Title: "Signer le Formulaire",
    name: "Prénom",
    surname: "Nom",
    email: "Email",
    phone: "Téléphone",
    fiscalCode: "Code Fiscal / ID",
    birthDate: "Date de Naissance",
    birthPlace: "Lieu de Naissance",
    residenceCity: "Ville de Résidence",
    residenceAddress: "Adresse",
    activity: "Activité (équipement inclus)",
    numParticipants: "Nombre de participants",
    countryLabel: "Pays d'Origine *",
    activitySelectionWarning: "⚠️ Sélectionnez attentivement l'activité et la durée: ces données déterminent le prix et ne pourront pas être modifiées après l'envoi.",
    participantAge: "Âge",
    participantWeight: "Poids (kg)",
    agePlaceholder: "Ex: 25",
    weightPlaceholder: "Ex: 70",
    level: "Niveau",
    levelBeginner: "Complètement débutant",
    levelExperienced: "J'ai déjà pratiqué",
    levelDetails: "Décrivez votre expérience...",
    preferredDate: "Date Préférée",
    time: "Horaire",
    selectTime: "Choisir",
    duration: "Durée",
    hour1: "1 heure",
    hours2: "2 heures privées - 130€",
    hours2semi: "2 heures semi-privées - 90€ chacun",
    hours6: "Cours de base 6 heures (2-3 jours)",
    // Activity translations
    kitesurfPrivate: "🪁 Kitesurf Privé",
    kitesurfSemiPrivate: "🪁 Kitesurf Semi-Privé",
    wingsurfPrivate: "🏄 WIN(G)SURF Privé (facile et amusant)",
    wingsurfGroup: "🏄 WIN(G)SURF Groupe (facile et amusant)",
    wingfoilPrivate: "🪽 Wingfoil Privé",
    wingfoilGroup: "🪽 Wingfoil Groupe",
    windsurfPrivate: "🌊 Windsurf Privé (cours débutants uniquement)",
    windsurfSemiPrivate: "🌊 Windsurf Semi-Privé",
    // Duration translations
    duration_2h: "2 heures",
    duration_6h: "Cours de base 6 heures",
    duration_1h: "1 heure",
    perPerson: "chacun",
    regIncluded: "inscr. €10 incluse",
    // Multi-day stay translations
    stayMultipleDays: "Je veux rester plusieurs jours",
    stayDuration: "Combien de temps restez-vous?",
    stay2to6days: "2-6 jours",
    stay1week: "1 semaine",
    stay2weeks: "2+ semaines",
    additionalDates: "Sélectionnez d'autres dates disponibles",
    accommodationShort: "🏠 Pour l'hébergement, consultez: sardiniakiteschool.net/accomodation/ ou venez à la plage et demandez la caravane ou le van pour rester près de l'école!",
    accommodationLong: "🏡 Vous pouvez réserver notre maison de campagne à 15 min de l'école!",
    accommodationPrices: "💰 Chambre privée: €60/jour (basse saison Avr-Mai-Sep-Oct) | €100/jour (haute saison). Maison entière (max 6 pers.): €200/jour basse saison | €400/jour haute saison. Min. 7 jours.",
    accommodationLink: "ℹ️ Info: sardiniakiteschool.net/accomodation/",
    declarationsTitle: "Déclarations Obligatoires",
    insuranceNotice: "⚠️ ASSURANCE MATÉRIEL NON INCLUSE. La réparation démarre à 100 € en cas de dommage. Pour souscrire l'assurance, vous devez PAYER À L'AVANCE le cours + assurance SAFETY TOOL VDWS directement à l'école : coût 39 € pour 1 an.",
    healthDeclaration: "Je déclare être en bonne condition physique et ne présenter aucune contre-indication à la pratique sportive non compétitive.",
    regulationsDeclaration: "Je déclare connaître et accepter le règlement de l'association Move Sardinia A.S.D.",
    liabilityDeclaration: "Je décharge l'ASD Move Sardinia de toute responsabilité pour les dommages causés à des tiers ou à moi-même pendant l'activité.",
    privacyDeclaration: "Je consens au traitement des données personnelles conformément au décret législatif 196/2003 et au RGPD.",
    optionalTitle: "Consentements Optionnels (non obligatoires)",
    mediaConsent: "📸 J'accepte d'être filmé/photographié et que les images soient utilisées sur les réseaux sociaux et le site web de l'école.",
    newsletterConsent: "📧 Je souhaite recevoir des newsletters et mises à jour de l'école.",
    membershipFee: "✅ Cotisation €10 déjà incluse dans le prix",
    notRecommended: "Non recommandé",
    duration_4h: "4 heures - Cours Flash",
    windsurfGroup: "🌊 Windsurf Groupe (cours débutants uniquement)",
    youthPricing: "Tarif jeunes (7-14 ans): €65/heure cours privé",
    weightWarning: "Le kitesurf n'est pas recommandé pour les poids inférieurs à 30 kg. Nous vous suggérons le wingsurf ou le windsurf!",
    minorReminder: "Pour les mineurs: insérez les données de l'enfant et la signature du parent.",
    kiteTrainerFree: "Quand l'école n'est pas occupée, demandez le kite trainer pour apprendre à manœuvrer le kite GRATUITEMENT! Dépliant informatif avec les bases du kitesurf inclus dans le cours.",
    returningStudent: "J'ai déjà pris des cours chez vous - €65/h toutes les 2 heures",
    rentalUpsellTitle: "Location d'Equipement (optionnel)",
    rentalUpsellDesc: "Ajoutez 1 heure de location d'equipement apres votre cours a prix reduit!",
    rentalUpsellPrice: "€20/equipement au lieu de €30",
    rentalUpsellAdd: "Ajouter la location",
    rentalEquipmentCount: "Combien d'equipements voulez-vous louer?",
    rentalEquipmentSingle: "equipement",
    rentalEquipmentPlural: "equipements",
    scheduleWarning: "⚠️ Note importante: Les horaires et jours indiqués sont des préférences et peuvent varier en fonction des réservations existantes et des conditions de vent. Nous vous confirmerons la date et l'heure définitives.",
    ikoCertificateNote: "🏅 Ajoutez 5 € pour recevoir le certificat IKO international (reconnu dans le monde entier)",
    signatureInstructions: "Utilisez votre doigt ou la souris pour signer dans le cadre ci-dessous.",
    clearSignature: "Effacer la Signature",
    signHere: "Signez ici",
    signatureNote: "Note: En signant ce formulaire, vous acceptez toutes les conditions indiquées et confirmez l'exactitude des données saisies.",
    back: "Retour",
    next: "Suivant",
    submit: "Envoyer le Formulaire",
    sending: "Envoi...",
    successMessage: "Formulaire envoyé avec succès! Nous vous contacterons bientôt 🏄‍♀️",
    errorRequired: "Veuillez remplir tous les champs obligatoires",
    errorPhone: "📱 Numéro non valide. Ajoutez le préfixe international (ex. +39 Italie, +34 Espagne, +33 France, +49 Allemagne, +44 UK).",
    phoneHint: "Saisissez le numéro avec le préfixe international (ex.",
    errorDeclarations: "Vous devez accepter toutes les déclarations",
    errorSignature: "Veuillez signer le formulaire pour continuer",
    errorSubmit: "Erreur lors de l'envoi du formulaire. Veuillez réessayer."
  },
  de: {
    title: "Anmeldeformular",
    subtitle: "Move Sardinia A.S.D.",
    step1Title: "Persönliche Daten",
    step2Title: "Unterrichtsart",
    noDepositNotice: "💳 In dieser Phase wird keine Anzahlung verlangt.",
    step3Title: "Formular Unterschreiben",
    name: "Vorname",
    surname: "Nachname",
    email: "E-Mail",
    phone: "Telefon",
    fiscalCode: "Steuernummer / ID",
    birthDate: "Geburtsdatum",
    birthPlace: "Geburtsort",
    residenceCity: "Wohnort",
    residenceAddress: "Adresse",
    activity: "Aktivität (Ausrüstung inklusive)",
    numParticipants: "Anzahl der Teilnehmer",
    countryLabel: "Herkunftsland *",
    activitySelectionWarning: "⚠️ Wählen Sie Aktivität und Dauer sorgfältig aus: Diese Angaben bestimmen den Preis und können nach dem Absenden nicht mehr geändert werden.",
    participantAge: "Alter",
    participantWeight: "Gewicht (kg)",
    agePlaceholder: "Z.B.: 25",
    weightPlaceholder: "Z.B.: 70",
    level: "Niveau",
    levelBeginner: "Kompletter Anfänger",
    levelExperienced: "Ich habe schon Erfahrung",
    levelDetails: "Beschreiben Sie Ihre Erfahrung...",
    preferredDate: "Bevorzugtes Datum",
    time: "Uhrzeit",
    selectTime: "Wählen",
    duration: "Dauer",
    hour1: "1 Stunde",
    hours2: "2 Stunden privat - 130€",
    hours2semi: "2 Stunden halb-privat - 90€ pro Person",
    hours6: "Grundkurs 6 Stunden (2-3 Tage)",
    // Activity translations
    kitesurfPrivate: "🪁 Kitesurf Privat",
    kitesurfSemiPrivate: "🪁 Kitesurf Halb-Privat",
    wingsurfPrivate: "🏄 WIN(G)SURF Privat (einfach & spaßig)",
    wingsurfGroup: "🏄 WIN(G)SURF Gruppe (einfach & spaßig)",
    wingfoilPrivate: "🪽 Wingfoil Privat",
    wingfoilGroup: "🪽 Wingfoil Gruppe",
    windsurfPrivate: "🌊 Windsurf Privat (nur Anfängerkurse)",
    windsurfSemiPrivate: "🌊 Windsurf Halb-Privat",
    // Duration translations
    duration_2h: "2 Stunden",
    duration_6h: "Grundkurs 6 Stunden",
    duration_1h: "1 Stunde",
    perPerson: "pro Person",
    regIncluded: "Anm. €10 inkl.",
    // Multi-day stay translations
    stayMultipleDays: "Ich möchte mehrere Tage bleiben",
    stayDuration: "Wie lange bleiben Sie?",
    stay2to6days: "2-6 Tage",
    stay1week: "1 Woche",
    stay2weeks: "2+ Wochen",
    additionalDates: "Wählen Sie weitere verfügbare Daten",
    accommodationShort: "🏠 Für Unterkunft, siehe: sardiniakiteschool.net/accomodation/ oder kommen Sie zum Strand und fragen Sie nach dem Wohnwagen oder Van, um in der Nähe der Schule zu bleiben!",
    accommodationLong: "🏡 Sie können unser Landhaus 15 Min. von der Schule buchen!",
    accommodationPrices: "💰 Privatzimmer: €60/Tag (Nebensaison Apr-Mai-Sep-Okt) | €100/Tag (Hochsaison). Ganzes Haus (max 6 Pers.): €200/Tag Nebensaison | €400/Tag Hochsaison. Min. 7 Tage.",
    accommodationLink: "ℹ️ Info: sardiniakiteschool.net/accomodation/",
    declarationsTitle: "Pflichtangaben",
    insuranceNotice: "⚠️ AUSRÜSTUNGSVERSICHERUNG NICHT ENTHALTEN. Reparaturen beginnen bei 100 € im Schadensfall. Wer eine Versicherung möchte, muss den Kurs + SAFETY TOOL VDWS-Versicherung IM VORAUS direkt an die Schule ZAHLEN: 39 € für 1 Jahr.",
    healthDeclaration: "Ich erkläre, dass ich in guter körperlicher Verfassung bin und keine Kontraindikationen für nicht-wettkampfmäßigen Sport habe.",
    regulationsDeclaration: "Ich erkläre, dass ich die Regeln des Vereins Move Sardinia A.S.D. kenne und akzeptiere.",
    liabilityDeclaration: "Ich entlaste ASD Move Sardinia von jeglicher Haftung für Schäden an Dritten oder mir selbst während der Aktivität.",
    privacyDeclaration: "Ich stimme der Verarbeitung personenbezogener Daten gemäß dem Gesetzesdekret 196/2003 und der DSGVO zu.",
    optionalTitle: "Optionale Zustimmungen (nicht verpflichtend)",
    mediaConsent: "📸 Ich stimme zu, gefilmt/fotografiert zu werden und dass die Bilder auf den Social-Media- und Webkanälen der Schule verwendet werden dürfen.",
    newsletterConsent: "📧 Ich möchte Newsletter und Updates von der Schule erhalten.",
    membershipFee: "✅ Mitgliedsbeitrag €10 bereits im Preis enthalten",
    notRecommended: "Nicht empfohlen",
    duration_4h: "4 Stunden - Flash Kurs",
    windsurfGroup: "🌊 Windsurf Gruppe (nur Anfängerkurse)",
    youthPricing: "Jugendpreis (7-14 Jahre): €65/Stunde Privatstunde",
    weightWarning: "Kitesurfen wird bei einem Gewicht unter 30 kg nicht empfohlen. Wir empfehlen Wingsurf oder Windsurf!",
    minorReminder: "Für Minderjährige: Daten des Kindes und Unterschrift des Elternteils eingeben.",
    kiteTrainerFree: "Wenn die Schule nicht beschäftigt ist, fragen Sie nach dem Kite-Trainer, um den Kite KOSTENLOS zu steuern! Informationsbroschüre mit Kitesurf-Grundlagen im Kurs enthalten.",
    returningStudent: "Ich hatte schon Unterricht bei euch - €65/h alle 2 Stunden",
    rentalUpsellTitle: "Ausrustungsverleih (optional)",
    rentalUpsellDesc: "Fugen Sie 1 Stunde Ausrustungsverleih nach Ihrer Stunde zum Sonderpreis hinzu!",
    rentalUpsellPrice: "€20/Ausrustung statt €30",
    rentalUpsellAdd: "Verleih hinzufugen",
    rentalEquipmentCount: "Wie viele Ausrustungen mochten Sie mieten?",
    rentalEquipmentSingle: "Ausrustung",
    rentalEquipmentPlural: "Ausrustungen",
    scheduleWarning: "⚠️ Wichtiger Hinweis: Die angegebenen Zeiten und Tage sind Präferenzen und können je nach bestehenden Buchungen und Windbedingungen variieren. Wir werden das endgültige Datum und die Uhrzeit bestätigen.",
    ikoCertificateNote: "🏅 Fügen Sie 5 € hinzu, um das internationale IKO-Zertifikat zu erhalten (weltweit anerkannt)",
    signatureInstructions: "Verwenden Sie Ihren Finger oder die Maus, um im untenstehenden Feld zu unterschreiben.",
    clearSignature: "Unterschrift Löschen",
    signHere: "Hier unterschreiben",
    signatureNote: "Hinweis: Mit Ihrer Unterschrift akzeptieren Sie alle angegebenen Bedingungen und bestätigen die Richtigkeit der eingegebenen Daten.",
    back: "Zurück",
    next: "Weiter",
    submit: "Formular Absenden",
    sending: "Senden...",
    successMessage: "Formular erfolgreich gesendet! Wir werden Sie bald kontaktieren 🏄‍♀️",
    errorRequired: "Bitte füllen Sie alle Pflichtfelder aus",
    errorPhone: "📱 Ungültige Nummer. Fügen Sie die internationale Vorwahl hinzu (z.B. +39 Italien, +34 Spanien, +33 Frankreich, +49 Deutschland, +44 UK).",
    phoneHint: "Geben Sie die Nummer mit internationaler Vorwahl ein (z.B.",
    errorDeclarations: "Sie müssen alle Erklärungen akzeptieren",
    errorSignature: "Bitte unterschreiben Sie das Formular, um fortzufahren",
    errorSubmit: "Fehler beim Senden des Formulars. Bitte versuchen Sie es erneut."
  },
  es: {
    title: "Formulario de Inscripción",
    subtitle: "Move Sardinia A.S.D.",
    step1Title: "Datos Personales",
    step2Title: "Tipo de Clase",
    noDepositNotice: "💳 En esta fase no se requiere ningún depósito.",
    step3Title: "Firmar el Formulario",
    name: "Nombre",
    surname: "Apellido",
    email: "Correo Electrónico",
    phone: "Teléfono",
    fiscalCode: "Código Fiscal / ID",
    birthDate: "Fecha de Nacimiento",
    birthPlace: "Lugar de Nacimiento",
    residenceCity: "Ciudad de Residencia",
    residenceAddress: "Dirección",
    activity: "Actividad (equipo incluido)",
    numParticipants: "Número de participantes",
    countryLabel: "País de Origen *",
    activitySelectionWarning: "⚠️ Selecciona atentamente actividad y duración: estos datos determinan el precio y no podrán modificarse después del envío.",
    participantAge: "Edad",
    participantWeight: "Peso (kg)",
    agePlaceholder: "Ej: 25",
    weightPlaceholder: "Ej: 70",
    level: "Nivel",
    levelBeginner: "Completamente principiante",
    levelExperienced: "Ya tengo experiencia",
    levelDetails: "Describe tu experiencia...",
    preferredDate: "Fecha Preferida",
    time: "Horario",
    selectTime: "Elegir",
    duration: "Duración",
    hour1: "1 hora",
    hours2: "2 horas privadas - 130€",
    hours2semi: "2 horas semi-privadas - 90€ cada uno",
    hours6: "Curso básico 6 horas (2-3 días)",
    // Activity translations
    kitesurfPrivate: "🪁 Kitesurf Privado",
    kitesurfSemiPrivate: "🪁 Kitesurf Semi-Privado",
    wingsurfPrivate: "🏄 WIN(G)SURF Privado (fácil y divertido)",
    wingsurfGroup: "🏄 WIN(G)SURF Grupo (fácil y divertido)",
    wingfoilPrivate: "🪽 Wingfoil Privado",
    wingfoilGroup: "🪽 Wingfoil Grupo",
    windsurfPrivate: "🌊 Windsurf Privado (solo clases para principiantes)",
    windsurfSemiPrivate: "🌊 Windsurf Semi-Privado",
    // Duration translations
    duration_2h: "2 horas",
    duration_6h: "Curso básico 6 horas",
    duration_1h: "1 hora",
    perPerson: "cada uno",
    regIncluded: "inscr. €10 incluida",
    // Multi-day stay translations
    stayMultipleDays: "Quiero quedarme varios días",
    stayDuration: "¿Cuánto tiempo te quedas?",
    stay2to6days: "2-6 días",
    stay1week: "1 semana",
    stay2weeks: "2+ semanas",
    additionalDates: "Selecciona otras fechas disponibles",
    accommodationShort: "🏠 Para alojamiento, consulta: sardiniakiteschool.net/accomodation/ o ven a la playa y pregunta por la caravana o van para quedarte cerca de la escuela!",
    accommodationLong: "🏡 ¡Puedes reservar nuestra casa de campo a 15 min de la escuela!",
    accommodationPrices: "💰 Habitación privada: €60/día (temporada baja Abr-May-Sep-Oct) | €100/día (temporada alta). Casa entera (máx 6 pers.): €200/día temp. baja | €400/día temp. alta. Mín. 7 días.",
    accommodationLink: "ℹ️ Info: sardiniakiteschool.net/accomodation/",
    declarationsTitle: "Declaraciones Obligatorias",
    insuranceNotice: "⚠️ SEGURO DEL EQUIPO NO INCLUIDO. La reparación empieza desde 100 € en caso de daño. Si deseas el seguro, debes PAGAR POR ADELANTADO la clase + seguro SAFETY TOOL VDWS directamente a la escuela: cuesta 39 € por 1 año.",
    healthDeclaration: "Declaro estar en buenas condiciones físicas y no tener contraindicaciones para la práctica deportiva no competitiva.",
    regulationsDeclaration: "Declaro conocer y aceptar el reglamento de la asociación Move Sardinia A.S.D.",
    liabilityDeclaration: "Exonero a ASD Move Sardinia de cualquier responsabilidad por daños a terceros o a mí mismo durante la actividad.",
    privacyDeclaration: "Consiento el tratamiento de datos personales de acuerdo con el Decreto Legislativo 196/2003 y GDPR.",
    optionalTitle: "Consentimientos Opcionales (no obligatorios)",
    mediaConsent: "📸 Consiento ser filmado/fotografiado y que las imágenes puedan ser utilizadas en las redes sociales y web de la escuela.",
    newsletterConsent: "📧 Deseo recibir boletines y actualizaciones de la escuela.",
    membershipFee: "✅ Cuota de membresía €10 ya incluida en el precio",
    notRecommended: "No recomendado",
    duration_4h: "4 horas - Curso Flash",
    windsurfGroup: "🌊 Windsurf Grupo (solo clases para principiantes)",
    youthPricing: "Precio jóvenes (7-14 años): €65/hora clase privada",
    weightWarning: "El kitesurf no se recomienda para pesos inferiores a 30 kg. Te sugerimos wingsurf o windsurf!",
    minorReminder: "Para menores: introducir los datos del hijo/a y la firma del padre/madre.",
    kiteTrainerFree: "Cuando la escuela no está ocupada, pide el kite trainer para aprender a manejar el kite GRATIS! Folleto informativo con las bases del kitesurf incluido en el curso.",
    returningStudent: "Ya tuve clases con vosotros - €65/h cada 2 horas",
    rentalUpsellTitle: "Alquiler de Equipo (opcional)",
    rentalUpsellDesc: "Anade 1 hora de alquiler de equipo despues de tu clase a precio reducido!",
    rentalUpsellPrice: "€20/equipo en vez de €30",
    rentalUpsellAdd: "Anadir alquiler",
    rentalEquipmentCount: "Cuantos equipos quieres alquilar?",
    rentalEquipmentSingle: "equipo",
    rentalEquipmentPlural: "equipos",
    scheduleWarning: "⚠️ Nota importante: Los horarios y días indicados son preferencias y pueden variar según las reservas existentes y las condiciones del viento. Confirmaremos la fecha y hora definitivas.",
    ikoCertificateNote: "🏅 Añade 5 € para recibir el certificado IKO internacional (reconocido en todo el mundo)",
    signatureInstructions: "Usa tu dedo o el ratón para firmar en el recuadro de abajo.",
    clearSignature: "Borrar Firma",
    signHere: "Firma aquí",
    signatureNote: "Nota: Al firmar este formulario, aceptas todas las condiciones indicadas y confirmas la veracidad de los datos introducidos.",
    back: "Atrás",
    next: "Siguiente",
    submit: "Enviar Formulario",
    sending: "Enviando...",
    successMessage: "¡Formulario enviado con éxito! Te contactaremos pronto 🏄‍♀️",
    errorRequired: "Por favor, completa todos los campos obligatorios",
    errorPhone: "📱 Número no válido. Añade el prefijo internacional (ej. +39 Italia, +34 España, +33 Francia, +49 Alemania, +44 UK).",
    phoneHint: "Introduce el número con prefijo internacional (ej.",
    errorDeclarations: "Debes aceptar todas las declaraciones",
    errorSignature: "Por favor, firma el formulario para continuar",
    errorSubmit: "Error al enviar el formulario. Por favor, inténtalo de nuevo."
  }
};

export default function RegistrationForm({ isOpen, onClose, sessionId, language = "it", suggestedActivity = "" }) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRegulations, setShowRegulations] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    fiscal_code: "",
    birth_date: "",
    birth_place: "",
    residence_city: "",
    residence_address: "",
    phone: "",
    email: "",
    country: "IT",
    lesson_type: suggestedActivity || "wingsurf_privato",
    num_participants: 1,
    participant_age: "",
    participant_weight: "",
    experience_level: "beginner",
    experience_details: "",
    preferred_date: "",
    preferred_time: "",
    preferred_time_2: "",
    preferred_time_day2: "",
    preferred_time_day2_2: "",
    duration: "",
    stay_multiple_days: false,
    stay_duration: "",
    additional_dates: "",
    accepts_health_declaration: false,
    accepts_regulations: false,
    accepts_liability_waiver: false,
    accepts_privacy: false,
    accepts_media_consent: false,
    accepts_newsletter: false,
    rental_equipment: false,
    rental_equipment_count: 1,
    signature_data: "",
  });

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  const t = formTranslations[language] || formTranslations.it;

  // Country code → expected international prefix
  const COUNTRY_PREFIX = {
    IT: '+39', FR: '+33', DE: '+49', ES: '+34', GB: '+44', US: '+1', CH: '+41',
    AT: '+43', BE: '+32', NL: '+31', LU: '+352', PT: '+351', IE: '+353', PL: '+48',
    CZ: '+420', DK: '+45', SE: '+46', NO: '+47', FI: '+358', MT: '+356', MC: '+377',
    CA: '+1', AU: '+61', NZ: '+64', MX: '+52', AR: '+54', BR: '+55',
  };

  // Validate phone: must start with + followed by 1-3 digit country code and at least 6 more digits (total 7-15)
  const validatePhone = (raw) => {
    if (!raw) return false;
    const cleaned = raw.replace(/[\s\-().]/g, '');
    return /^\+\d{7,15}$/.test(cleaned);
  };
  const isPhoneValid = validatePhone(formData.phone);
  const expectedPrefix = COUNTRY_PREFIX[formData.country] || '+39';

  useEffect(() => {
    if (suggestedActivity && isOpen) {
      setFormData(prev => ({ ...prev, lesson_type: suggestedActivity, duration: "" }));
    }
  }, [suggestedActivity, isOpen]);

  useEffect(() => {
    if (step === 3 && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
  }, [step]);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-set num_participants based on lesson type
      if (field === "lesson_type") {
        // Reset duration when changing lesson type (different options)
        updated.duration = "";
        
        if (value.includes("privato") && !value.includes("semiprivato")) {
          updated.num_participants = 1;
        } else if (value.includes("semiprivato") || value.includes("gruppo")) {
          if (updated.num_participants < 2) {
            updated.num_participants = 2;
          }
        }
        // Reset rental when switching away from wingsurf/windsurf
        if (!value.includes("wingsurf") && !value.includes("windsurf") || value.includes("wingfoil")) {
          updated.rental_equipment = false;
          updated.rental_equipment_count = 1;
        }
      }
      // Reset duration when age changes (youth pricing changes options)
      if (field === "participant_age") {
        updated.duration = "";
      }
      // Reset duration when participants change (group pricing changes)
      if (field === "num_participants") {
        updated.duration = "";
      }
      
      return updated;
    });
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.closePath();
      setIsDrawing(false);
      setFormData(prev => ({ ...prev, signature_data: canvas.toDataURL("image/png") }));
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    setFormData(prev => ({ ...prev, signature_data: "" }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.surname || !formData.email || !formData.phone) {
      toast.error(t.errorRequired);
      return;
    }
    if (!validatePhone(formData.phone)) {
      toast.error(t.errorPhone);
      setStep(2);
      return;
    }
    if (!formData.accepts_health_declaration || !formData.accepts_regulations || 
        !formData.accepts_liability_waiver || !formData.accepts_privacy) {
      toast.error(t.errorDeclarations);
      return;
    }
    if (!formData.signature_data) {
      toast.error(t.errorSignature);
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/registration`, {
        ...formData,
        session_id: sessionId,
        language: language
      });
      toast.success(t.successMessage);
      onClose();
      setStep(1);
      setFormData({
        name: "", surname: "", fiscal_code: "", birth_date: "", birth_place: "",
        residence_city: "", residence_address: "", phone: "", email: "", country: "IT",
        lesson_type: suggestedActivity || "wingsurf_privato", num_participants: 1, participant_age: "", participant_weight: "", experience_level: "beginner", experience_details: "",
        preferred_date: "", preferred_time: "", preferred_time_2: "", preferred_time_day2: "", preferred_time_day2_2: "", duration: "2h_130",
        stay_multiple_days: false, stay_duration: "", additional_dates: "",
        accepts_health_declaration: false, accepts_regulations: false,
        accepts_liability_waiver: false, accepts_privacy: false,
        accepts_media_consent: false, accepts_newsletter: false,
        rental_equipment: false, rental_equipment_count: 1,
        signature_data: ""
      });
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(t.errorSubmit);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="bg-white w-full max-w-md max-h-[90vh] rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-teal-500 to-cyan-600 text-white p-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">{t.title}</h2>
              <p className="text-sm text-white/80">{t.subtitle}</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress */}
          <div className="flex gap-2 p-4 bg-gray-50">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex-1 h-2 rounded-full overflow-hidden bg-gray-200">
                <div 
                  className={`h-full transition-all duration-300 ${s <= step ? "bg-teal-500" : "bg-gray-200"}`}
                  style={{ width: s <= step ? "100%" : "0%" }}
                />
              </div>
            ))}
          </div>

          {/* Form Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-center" data-testid="no-deposit-notice">
              <p className="text-xs text-emerald-800 font-medium">{t.noDepositNotice}</p>
            </div>

            {/* Step 2: Personal Data (moved after lesson selection) */}
            {step === 2 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                <h3 className="font-semibold text-gray-900">{t.step1Title}</h3>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="name">{t.name} *</Label>
                    <Input id="name" value={formData.name} onChange={e => handleChange("name", e.target.value)} placeholder="Mario" />
                  </div>
                  <div>
                    <Label htmlFor="surname">{t.surname} *</Label>
                    <Input id="surname" value={formData.surname} onChange={e => handleChange("surname", e.target.value)} placeholder="Rossi" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">{t.email} *</Label>
                  <Input id="email" type="email" value={formData.email} onChange={e => handleChange("email", e.target.value)} placeholder="mario@email.com" />
                </div>

                <div>
                  <Label htmlFor="phone">{t.phone} *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={e => handleChange("phone", e.target.value)}
                    placeholder={`${expectedPrefix} 123 456 7890`}
                    className={formData.phone && !isPhoneValid ? "border-red-400 focus-visible:ring-red-400" : ""}
                    data-testid="phone-input"
                  />
                  {formData.phone && !isPhoneValid && (
                    <p className="mt-1.5 text-xs text-red-600 leading-snug" data-testid="phone-error">
                      {t.errorPhone}
                    </p>
                  )}
                  {!formData.phone && (
                    <p className="mt-1.5 text-xs text-gray-500" data-testid="phone-hint">
                      💡 {t.phoneHint} <strong>{expectedPrefix}</strong>)
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="country-select-step1">{t.countryLabel}</Label>
                  <select
                    id="country-select-step1"
                    value={formData.country}
                    onChange={e => handleChange("country", e.target.value)}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    data-testid="country-select"
                  >
                    <option value="IT">🇮🇹 Italia</option>
                    <option value="" disabled>──────────</option>
                    <option value="FR">🇫🇷 Francia / France</option>
                    <option value="DE">🇩🇪 Germania / Deutschland</option>
                    <option value="ES">🇪🇸 Spagna / España</option>
                    <option value="GB">🇬🇧 Regno Unito / United Kingdom</option>
                    <option value="US">🇺🇸 Stati Uniti / United States</option>
                    <option value="CH">🇨🇭 Svizzera / Switzerland</option>
                    <option value="AT">🇦🇹 Austria</option>
                    <option value="BE">🇧🇪 Belgio / Belgium</option>
                    <option value="NL">🇳🇱 Paesi Bassi / Netherlands</option>
                    <option value="LU">🇱🇺 Lussemburgo / Luxembourg</option>
                    <option value="PT">🇵🇹 Portogallo / Portugal</option>
                    <option value="IE">🇮🇪 Irlanda / Ireland</option>
                    <option value="PL">🇵🇱 Polonia / Poland</option>
                    <option value="CZ">🇨🇿 Repubblica Ceca / Czech Republic</option>
                    <option value="DK">🇩🇰 Danimarca / Denmark</option>
                    <option value="SE">🇸🇪 Svezia / Sweden</option>
                    <option value="NO">🇳🇴 Norvegia / Norway</option>
                    <option value="FI">🇫🇮 Finlandia / Finland</option>
                    <option value="MT">🇲🇹 Malta</option>
                    <option value="MC">🇲🇨 Monaco</option>
                    <option value="CA">🇨🇦 Canada</option>
                    <option value="AU">🇦🇺 Australia</option>
                    <option value="NZ">🇳🇿 Nuova Zelanda / New Zealand</option>
                    <option value="MX">🇲🇽 Messico / Mexico</option>
                    <option value="AR">🇦🇷 Argentina</option>
                    <option value="BR">🇧🇷 Brasile / Brazil</option>
                    <option value="OTHER">🌍 Altro / Other</option>
                  </select>
                </div>

                {formData.country === "IT" && (
                  <div>
                    <Label htmlFor="fiscal_code">{t.fiscalCode}</Label>
                    <Input id="fiscal_code" value={formData.fiscal_code} onChange={e => handleChange("fiscal_code", e.target.value)} placeholder="RSSMRA80A01H501U" />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="birth_date">{t.birthDate}</Label>
                    <Input id="birth_date" type="date" value={formData.birth_date} onChange={e => handleChange("birth_date", e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="birth_place">{t.birthPlace}</Label>
                    <Input id="birth_place" value={formData.birth_place} onChange={e => handleChange("birth_place", e.target.value)} placeholder="Roma" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="residence_city">{t.residenceCity}</Label>
                  <Input id="residence_city" value={formData.residence_city} onChange={e => handleChange("residence_city", e.target.value)} placeholder="Olbia" />
                </div>

                <div>
                  <Label htmlFor="residence_address">{t.residenceAddress}</Label>
                  <Input id="residence_address" value={formData.residence_address} onChange={e => handleChange("residence_address", e.target.value)} placeholder="Via Roma 1" />
                </div>
              </motion.div>
            )}

            {/* Step 1: Lesson & Declarations (moved BEFORE personal data) */}
            {step === 1 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                <h3 className="font-semibold text-gray-900">{t.step2Title}</h3>

                <motion.div
                  animate={{ boxShadow: ["0 0 0 0 rgba(251,191,36,0.4)", "0 0 0 8px rgba(251,191,36,0)", "0 0 0 0 rgba(251,191,36,0)"] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                  className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-400 rounded-lg p-3 text-sm text-amber-900 font-medium"
                  data-testid="activity-selection-warning"
                >
                  {t.activitySelectionWarning}
                </motion.div>
                
                <div>
                  <Label>{t.activity}</Label>
                  <Select value={formData.lesson_type} onValueChange={v => {
                    handleChange("lesson_type", v);
                    // Reset duration when activity changes
                    if (v === "kitesurf_semiprivato") {
                      handleChange("duration", "2h_90");
                    } else if (v === "kitesurf_privato") {
                      handleChange("duration", "2h_130");
                    } else if (v === "wingsurf_privato") {
                      handleChange("duration", "1h_70");
                    } else if (v === "wingsurf_gruppo") {
                      handleChange("duration", "1h_50");
                    } else if (v === "wingfoil_privato") {
                      handleChange("duration", "1h_70");
                    } else if (v === "wingfoil_gruppo") {
                      handleChange("duration", "1h_60");
                    } else if (v.startsWith("windsurf")) {
                      handleChange("duration", "1h_70");
                    }
                  }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="wingsurf_privato">{t.wingsurfPrivate}</SelectItem>
                      <SelectItem value="wingsurf_gruppo">{t.wingsurfGroup}</SelectItem>
                      <SelectItem value="wingfoil_privato">{t.wingfoilPrivate}</SelectItem>
                      <SelectItem value="wingfoil_gruppo">{t.wingfoilGroup}</SelectItem>
                      <SelectItem value="kitesurf_privato">{t.kitesurfPrivate}</SelectItem>
                      <SelectItem value="kitesurf_semiprivato">{t.kitesurfSemiPrivate}</SelectItem>
                      <SelectItem value="windsurf_privato">{t.windsurfPrivate}</SelectItem>
                      <SelectItem value="windsurf_gruppo">{t.windsurfGroup || t.windsurfSemiPrivate}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Number of participants - shown right after activity (BEFORE duration) */}
                {(formData.lesson_type.includes("semiprivato") || formData.lesson_type.includes("gruppo")) && (
                  <div data-testid="num-participants-section">
                    <Label>{t.numParticipants}</Label>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {(() => {
                        if (formData.lesson_type === "wingsurf_gruppo") return [2, 3, 4, 5, 6];
                        if (formData.lesson_type === "wingfoil_gruppo") return [2, 4, 6];
                        if (formData.lesson_type === "windsurf_gruppo") return [2, 4, 6];
                        if (formData.lesson_type === "kitesurf_semiprivato") return [2, 3, 4, 6];
                        if (formData.lesson_type.includes("semiprivato")) return [2, 4, 6];
                        return [2, 3, 4, 5, 6];
                      })().map(num => {
                        const isNotRecommended = formData.lesson_type === "kitesurf_semiprivato" && num === 3;
                        return (
                          <button
                            key={num}
                            type="button"
                            onClick={() => handleChange("num_participants", num)}
                            className={`min-w-[70px] flex-1 py-3 px-4 rounded-lg border-2 font-semibold transition-all ${
                              formData.num_participants === num
                                ? isNotRecommended
                                  ? "border-orange-500 bg-orange-50 text-orange-700"
                                  : "border-teal-500 bg-teal-50 text-teal-700"
                                : "border-gray-200 hover:border-gray-300 text-gray-600"
                            }`}
                          >
                            {num}
                            {isNotRecommended && <span className="block text-[10px] font-medium text-orange-600 mt-0.5">non consigliato</span>}
                          </button>
                        );
                      })}
                    </div>
                    {formData.lesson_type === "kitesurf_semiprivato" && formData.num_participants === 3 && (
                      <p className="mt-2 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded p-2">
                        ⚠️ Corso Starter 6 ore con 3 persone e 1 kite alternato — €200/persona. Sconsigliato perché il tempo di kite effettivo per persona si riduce.
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <Label>{t.duration}</Label>
                  <Select value={formData.duration} onValueChange={v => handleChange("duration", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {/* KITESURF PRIVATO */}
                      {formData.lesson_type === "kitesurf_privato" && (
                        <>
                          {formData.participant_age && parseInt(formData.participant_age) >= 7 && parseInt(formData.participant_age) <= 14 && (
                            <SelectItem value="1h_65">{t.duration_1h} - €65 {t.youthPricing ? "👦" : ""} ({t.regIncluded})</SelectItem>
                          )}
                          <SelectItem value="2h_130">{t.duration_2h} - €130 ({t.regIncluded})</SelectItem>
                          <SelectItem value="4h_260">{t.duration_4h || "4 ore - Corso Flash"} - €260 ({t.regIncluded})</SelectItem>
                          <SelectItem value="4h_260_1day">🔥 Corso Flash 4h — Desidero farlo in 1 giorno (2 fasce orarie) - €260 ({t.regIncluded})</SelectItem>
                          <SelectItem value="6h_390">{t.duration_6h} - €390 Corso Base ({t.regIncluded})</SelectItem>
                          <SelectItem value="1h_70">{t.duration_1h} - €70 ({t.regIncluded}) - {t.notRecommended}</SelectItem>
                          <SelectItem value="2h_130_returning">{t.returningStudent || "Ho già fatto lezione con voi"} - €65/h ({t.duration_2h})</SelectItem>
                        </>
                      )}
                      {/* KITESURF SEMI-PRIVATO */}
                      {formData.lesson_type === "kitesurf_semiprivato" && (
                        <>
                          {formData.num_participants === 3 ? (
                            <>
                              <SelectItem value="1h_40_kitetest_3p">⚠️ 1 ora Kite Test - €40 {t.perPerson} - 3 persone (non consigliato: troppo poco tempo) ({t.regIncluded})</SelectItem>
                              <SelectItem value="6h_200">⚠️ Corso Starter 6h - €200 {t.perPerson} - 3 persone / 1 kite alternato ({t.regIncluded})</SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="2h_80">{t.duration_2h} - €80 {t.perPerson} ({t.regIncluded})</SelectItem>
                              <SelectItem value="6h_240">{t.duration_6h} - €240 {t.perPerson} ({t.regIncluded})</SelectItem>
                              <SelectItem value="1h_40_kitetest">⚠️ 1 ora Kite Test - €40 {t.perPerson} (non consigliato) ({t.regIncluded})</SelectItem>
                            </>
                          )}
                        </>
                      )}
                      {/* WINGSURF PRIVATO */}
                      {formData.lesson_type === "wingsurf_privato" && (
                        <>
                          {formData.participant_age && parseInt(formData.participant_age) >= 7 && parseInt(formData.participant_age) <= 14 ? (
                            <>
                              <SelectItem value="1h_65">{t.duration_1h} - €65 👦 ({t.regIncluded})</SelectItem>
                              <SelectItem value="2h_130">{t.duration_2h} - €130 ({t.regIncluded})</SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="1h_70">{t.duration_1h} - €70 ({t.regIncluded})</SelectItem>
                              <SelectItem value="2h_130">{t.duration_2h} - €130 ({t.regIncluded})</SelectItem>
                            </>
                          )}
                        </>
                      )}
                      {/* WINGSURF GRUPPO */}
                      {formData.lesson_type === "wingsurf_gruppo" && (
                        <>
                          {formData.num_participants <= 2 ? (
                            <>
                              <SelectItem value="1h_50">{t.duration_1h} - €50 {t.perPerson} ({t.regIncluded})</SelectItem>
                              <SelectItem value="2h_100">{t.duration_2h} - €100 {t.perPerson} ({t.regIncluded})</SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="1h_40">{t.duration_1h} - €40 {t.perPerson} ({t.regIncluded})</SelectItem>
                              <SelectItem value="2h_80">{t.duration_2h} - €80 {t.perPerson} ({t.regIncluded})</SelectItem>
                            </>
                          )}
                        </>
                      )}
                      {/* WINGFOIL PRIVATO */}
                      {formData.lesson_type === "wingfoil_privato" && (
                        <>
                          <SelectItem value="1h_70">{t.duration_1h} - €70 ({t.regIncluded})</SelectItem>
                          <SelectItem value="2h_140">{t.duration_2h} - €140 ({t.regIncluded})</SelectItem>
                        </>
                      )}
                      {/* WINGFOIL GRUPPO */}
                      {formData.lesson_type === "wingfoil_gruppo" && (
                        <>
                          <SelectItem value="1h_60">{t.duration_1h} - €60 {t.perPerson} ({t.regIncluded})</SelectItem>
                          <SelectItem value="2h_120">{t.duration_2h} - €120 {t.perPerson} ({t.regIncluded})</SelectItem>
                        </>
                      )}
                      {/* WINDSURF PRIVATO */}
                      {formData.lesson_type === "windsurf_privato" && (
                        <>
                          {formData.participant_age && parseInt(formData.participant_age) >= 4 && parseInt(formData.participant_age) <= 6 ? (
                            <SelectItem value="giocosurf_30min_30">🧒 Gioco-Surf 30 min (4-6 anni) - €30 ({t.regIncluded})</SelectItem>
                          ) : formData.participant_age && parseInt(formData.participant_age) >= 7 && parseInt(formData.participant_age) <= 14 ? (
                            <>
                              <SelectItem value="1h_65">{t.duration_1h} - €65 👦 ({t.regIncluded})</SelectItem>
                              <SelectItem value="2h_130">{t.duration_2h} - €130 ({t.regIncluded})</SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="1h_70">{t.duration_1h} - €70 ({t.regIncluded})</SelectItem>
                              <SelectItem value="2h_130">{t.duration_2h} - €130 ({t.regIncluded})</SelectItem>
                            </>
                          )}
                        </>
                      )}
                      {/* WINDSURF GRUPPO */}
                      {formData.lesson_type === "windsurf_gruppo" && (
                        <>
                          <SelectItem value="1h_50">{t.duration_1h} - €50 {t.perPerson} ({t.regIncluded})</SelectItem>
                          <SelectItem value="2h_100">{t.duration_2h} - €100 {t.perPerson} ({t.regIncluded})</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Rental Equipment Upsell - only for wingsurf/windsurf */}
                {(formData.lesson_type.includes("wingsurf") || formData.lesson_type.includes("windsurf")) && !formData.lesson_type.includes("wingfoil") && (
                  <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl" data-testid="rental-upsell">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="rental_equipment"
                        checked={formData.rental_equipment}
                        onCheckedChange={c => handleChange("rental_equipment", c)}
                        data-testid="rental-checkbox"
                      />
                      <div className="flex-1">
                        <label htmlFor="rental_equipment" className="text-sm font-semibold text-gray-900 cursor-pointer block">
                          {t.rentalUpsellTitle}
                        </label>
                        <p className="text-xs text-gray-600 mt-1">{t.rentalUpsellDesc}</p>
                        <p className="text-xs font-bold text-green-700 mt-1">
                          {t.rentalUpsellPrice}
                        </p>
                      </div>
                    </div>

                    {formData.rental_equipment && (formData.lesson_type.includes("gruppo") || formData.lesson_type.includes("semiprivato")) && (
                      <div className="mt-3 ml-7">
                        <Label className="text-xs text-gray-700">{t.rentalEquipmentCount}</Label>
                        <div className="flex gap-2 mt-1">
                          {[1, 2, 3].map(num => (
                            <button
                              key={num}
                              type="button"
                              onClick={() => handleChange("rental_equipment_count", num)}
                              className={`w-10 h-10 rounded-lg text-sm font-bold transition-all ${
                                formData.rental_equipment_count === num
                                  ? "bg-amber-500 text-white shadow-md"
                                  : "bg-white text-gray-600 border border-gray-300 hover:border-amber-400"
                              }`}
                              data-testid={`rental-count-${num}`}
                            >
                              {num}
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          = €{formData.rental_equipment_count * 20} ({formData.rental_equipment_count} {formData.rental_equipment_count === 1 ? t.rentalEquipmentSingle : t.rentalEquipmentPlural})
                        </p>
                      </div>
                    )}

                    {formData.rental_equipment && !(formData.lesson_type.includes("gruppo") || formData.lesson_type.includes("semiprivato")) && (
                      <p className="text-xs text-gray-500 mt-2 ml-7">
                        = €20 (1 {t.rentalEquipmentSingle})
                      </p>
                    )}
                  </div>
                )}

                {/* Age and Weight */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t.participantAge}</Label>
                    <Input
                      type="number"
                      min="7"
                      max="99"
                      value={formData.participant_age}
                      onChange={e => handleChange("participant_age", e.target.value)}
                      placeholder={t.agePlaceholder}
                    />
                    {formData.participant_age && parseInt(formData.participant_age) >= 7 && parseInt(formData.participant_age) <= 14 && (
                      <p className="text-xs text-green-600 mt-1">{t.youthPricing}</p>
                    )}
                    {formData.participant_age && parseInt(formData.participant_age) < 18 && parseInt(formData.participant_age) >= 7 && (
                      <p className="text-xs text-amber-600 mt-1">{t.minorReminder}</p>
                    )}
                  </div>
                  <div>
                    <Label>{t.participantWeight} <span className="text-gray-400 text-xs">(opzionale)</span></Label>
                    <Input
                      type="number"
                      min="20"
                      max="150"
                      value={formData.participant_weight}
                      onChange={e => handleChange("participant_weight", e.target.value)}
                      placeholder={t.weightPlaceholder}
                    />
                    {formData.participant_weight && parseInt(formData.participant_weight) <= 30 && formData.lesson_type.includes("kite") && (
                      <p className="text-xs text-red-600 mt-1 font-semibold">{t.weightWarning}</p>
                    )}
                  </div>
                </div>

                {/* Experience Level */}
                <div className="space-y-3">
                  <Label>{t.level}</Label>
                  <div className="space-y-2">
                    <div 
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.experience_level === "beginner" 
                          ? "border-teal-500 bg-teal-50" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => {
                        handleChange("experience_level", "beginner");
                        handleChange("experience_details", "");
                      }}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.experience_level === "beginner" ? "border-teal-500" : "border-gray-300"
                      }`}>
                        {formData.experience_level === "beginner" && (
                          <div className="w-3 h-3 rounded-full bg-teal-500" />
                        )}
                      </div>
                      <span className="text-sm font-medium">🌱 {t.levelBeginner}</span>
                    </div>
                    
                    <div 
                      className={`flex flex-col gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.experience_level === "experienced" 
                          ? "border-teal-500 bg-teal-50" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => handleChange("experience_level", "experienced")}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          formData.experience_level === "experienced" ? "border-teal-500" : "border-gray-300"
                        }`}>
                          {formData.experience_level === "experienced" && (
                            <div className="w-3 h-3 rounded-full bg-teal-500" />
                          )}
                        </div>
                        <span className="text-sm font-medium">🏄 {t.levelExperienced}</span>
                      </div>
                      {formData.experience_level === "experienced" && (
                        <Input
                          value={formData.experience_details}
                          onChange={e => handleChange("experience_details", e.target.value)}
                          placeholder={t.levelDetails}
                          className="mt-2 text-sm"
                          onClick={e => e.stopPropagation()}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t.preferredDate}</Label>
                    <Input type="date" value={formData.preferred_date} onChange={e => handleChange("preferred_date", e.target.value)} />
                  </div>
                  <div>
                    <Label>{formData.duration && (formData.duration.startsWith("8h") || formData.duration === "4h_260_1day") ? "🚀 Compila le fasce nella sezione qui sotto ⬇️" : t.time}</Label>
                    <Select value={formData.preferred_time} onValueChange={v => handleChange("preferred_time", v)} disabled={formData.duration && (formData.duration.startsWith("8h") || formData.duration === "4h_260_1day")}>
                      <SelectTrigger><SelectValue placeholder={t.selectTime} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="11-13">11:00 - 13:00</SelectItem>
                        <SelectItem value="14-16">14:00 - 16:00</SelectItem>
                        <SelectItem value="16-18">16:00 - 18:00</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Dual-timeslot section: for 4h Flash 1-giorno AND 8h Full Immersion (2 giornate) */}
                {formData.duration && (formData.duration.startsWith("8h") || formData.duration === "4h_260_1day") && (
                  <div className="bg-cyan-50 border-2 border-cyan-300 rounded-lg p-3 space-y-3" data-testid="dual-timeslot-section">
                    <p className="text-xs font-semibold text-cyan-900">
                      {formData.duration === "4h_260_1day"
                        ? "🔥 Corso Flash 4h in 1 giorno — Seleziona 2 fasce orarie (2h + 2h)"
                        : "🚀 Corso Full Immersion — 4h al giorno per 2 giornate. Seleziona 2 fasce orarie per ogni giorno."}
                    </p>
                    <div>
                      <p className="text-[11px] font-bold text-cyan-800 mb-1">
                        {formData.duration === "4h_260_1day" ? "📅 GIORNO UNICO — 2 fasce (4h totali)" : "📅 GIORNO 1 — 2 fasce (4h totali)"}
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px]">Fascia 1</Label>
                          <Select value={formData.preferred_time} onValueChange={v => handleChange("preferred_time", v)}>
                            <SelectTrigger className="h-9 text-xs" data-testid="d1-slot1"><SelectValue placeholder={t.selectTime} /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="11-13">11:00 - 13:00</SelectItem>
                              <SelectItem value="14-16">14:00 - 16:00</SelectItem>
                              <SelectItem value="16-18">16:00 - 18:00</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px]">Fascia 2</Label>
                          <Select value={formData.preferred_time_2} onValueChange={v => handleChange("preferred_time_2", v)}>
                            <SelectTrigger className="h-9 text-xs" data-testid="d1-slot2"><SelectValue placeholder={t.selectTime} /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="11-13">11:00 - 13:00</SelectItem>
                              <SelectItem value="14-16">14:00 - 16:00</SelectItem>
                              <SelectItem value="16-18">16:00 - 18:00</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                    {formData.duration && formData.duration.startsWith("8h") && (
                      <div>
                        <p className="text-[11px] font-bold text-cyan-800 mb-1">📅 GIORNO 2 — 2 fasce (4h totali)</p>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-[10px]">Fascia 1</Label>
                            <Select value={formData.preferred_time_day2} onValueChange={v => handleChange("preferred_time_day2", v)}>
                              <SelectTrigger className="h-9 text-xs" data-testid="d2-slot1"><SelectValue placeholder={t.selectTime} /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="11-13">11:00 - 13:00</SelectItem>
                                <SelectItem value="14-16">14:00 - 16:00</SelectItem>
                                <SelectItem value="16-18">16:00 - 18:00</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-[10px]">Fascia 2</Label>
                            <Select value={formData.preferred_time_day2_2} onValueChange={v => handleChange("preferred_time_day2_2", v)}>
                              <SelectTrigger className="h-9 text-xs" data-testid="d2-slot2"><SelectValue placeholder={t.selectTime} /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="11-13">11:00 - 13:00</SelectItem>
                                <SelectItem value="14-16">14:00 - 16:00</SelectItem>
                                <SelectItem value="16-18">16:00 - 18:00</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                    )}
                    <p className="text-[10px] text-cyan-700">
                      💡 Esempio: <strong>11-13</strong> + <strong>14-16</strong>.
                    </p>
                  </div>
                )}

                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm text-amber-800">
                  {t.scheduleWarning}
                </div>

                {(formData.lesson_type === "kitesurf_privato" || formData.lesson_type === "kitesurf_semiprivato") && (
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-sm text-blue-800 font-medium" data-testid="iko-certificate-note">
                    {t.ikoCertificateNote}
                  </div>
                )}

                <div className="pt-4 border-t">
                  <h3 className="font-semibold text-gray-900 mb-3">{t.declarationsTitle}</h3>

                  <div className="mb-3 p-3 bg-amber-50 border-2 border-amber-400 rounded-lg" data-testid="insurance-notice">
                    <p className="text-xs text-amber-900 font-medium leading-relaxed">{t.insuranceNotice}</p>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Checkbox 
                        id="health" 
                        checked={formData.accepts_health_declaration}
                        onCheckedChange={c => handleChange("accepts_health_declaration", c)}
                      />
                      <label htmlFor="health" className="text-sm text-gray-700 cursor-pointer">
                        {t.healthDeclaration}
                      </label>
                    </div>

                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Checkbox 
                        id="regulations" 
                        checked={formData.accepts_regulations}
                        onCheckedChange={c => handleChange("accepts_regulations", c)}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <label htmlFor="regulations" className="text-sm text-gray-700 cursor-pointer">
                          {t.regulationsDeclaration}
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowRegulations(true)}
                          className="flex items-center gap-1 mt-1 text-xs text-teal-600 hover:text-teal-700 font-medium"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {language === "it" ? "Leggi il regolamento completo" : 
                           language === "en" ? "Read full regulations" :
                           language === "fr" ? "Lire le règlement complet" :
                           language === "de" ? "Vollständige Regeln lesen" :
                           "Leer reglamento completo"}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Checkbox 
                        id="liability" 
                        checked={formData.accepts_liability_waiver}
                        onCheckedChange={c => handleChange("accepts_liability_waiver", c)}
                      />
                      <label htmlFor="liability" className="text-sm text-gray-700 cursor-pointer">
                        {t.liabilityDeclaration}
                      </label>
                    </div>

                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Checkbox 
                        id="privacy" 
                        checked={formData.accepts_privacy}
                        onCheckedChange={c => handleChange("accepts_privacy", c)}
                      />
                      <label htmlFor="privacy" className="text-sm text-gray-700 cursor-pointer">
                        {t.privacyDeclaration}
                      </label>
                    </div>
                  </div>
                </div>

                {/* Optional Consents Section */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-3">{t.optionalTitle}</h4>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <Checkbox 
                        id="mediaConsent" 
                        checked={formData.accepts_media_consent}
                        onCheckedChange={c => handleChange("accepts_media_consent", c)}
                      />
                      <label htmlFor="mediaConsent" className="text-sm text-blue-700 cursor-pointer">
                        {t.mediaConsent}
                      </label>
                    </div>
                    <div className="flex items-start gap-3">
                      <Checkbox 
                        id="newsletter" 
                        checked={formData.accepts_newsletter}
                        onCheckedChange={c => handleChange("accepts_newsletter", c)}
                      />
                      <label htmlFor="newsletter" className="text-sm text-blue-700 cursor-pointer">
                        {t.newsletterConsent}
                      </label>
                    </div>
                  </div>
                </div>

                <div className="bg-teal-50 p-3 rounded-lg text-sm text-teal-800">
                  <strong>{t.membershipFee}</strong>
                </div>
              </motion.div>
            )}

            {/* Step 3: Signature */}
            {step === 3 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                {/* Deposit Status Badge */}
                <div className="bg-amber-100 border-2 border-amber-400 rounded-lg p-3 flex items-center gap-3">
                  <div className="bg-amber-400 text-amber-900 px-3 py-1 rounded-full text-sm font-bold">
                    💰 DEP. NON PAGATO
                  </div>
                  <span className="text-sm text-amber-800">
                    {language === "it" ? "Il deposito verrà richiesto dopo la conferma" :
                     language === "en" ? "Deposit will be requested after confirmation" :
                     language === "fr" ? "L'acompte sera demandé après confirmation" :
                     language === "de" ? "Anzahlung wird nach Bestätigung angefordert" :
                     "El depósito se solicitará después de la confirmación"}
                  </span>
                </div>

                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Pen className="w-5 h-5" />
                  {t.step3Title}
                </h3>
                
                <p className="text-sm text-gray-600">
                  {t.signatureInstructions}
                </p>

                <div className="relative">
                  <canvas
                    ref={canvasRef}
                    width={350}
                    height={200}
                    className="w-full border-2 border-dashed border-gray-300 rounded-lg bg-white touch-none cursor-crosshair"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                  {!hasSignature && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <p className="text-gray-400">{t.signHere}</p>
                    </div>
                  )}
                </div>

                <Button
                  variant="outline"
                  onClick={clearSignature}
                  className="w-full"
                  disabled={!hasSignature}
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  {t.clearSignature}
                </Button>

                <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-800">
                  <strong>{t.signatureNote}</strong>
                </div>
              </motion.div>
            )}
          </div>

          {/* Footer Buttons */}
          <div className="p-4 border-t bg-gray-50 flex gap-3">
            {step > 1 && (
              <Button variant="outline" onClick={() => setStep(s => s - 1)} className="flex-1">
                {t.back}
              </Button>
            )}
            
            {step < 3 ? (
              <Button 
                onClick={() => {
                  // Step 1 is now LESSON. Validate lesson + declarations before proceeding.
                  if (step === 1 && (!formData.accepts_health_declaration || !formData.accepts_regulations || 
                      !formData.accepts_liability_waiver || !formData.accepts_privacy)) {
                    toast.error(t.errorDeclarations);
                    return;
                  }
                  // Step 2 is now PERSONAL DATA. Validate phone here.
                  if (step === 2 && !validatePhone(formData.phone)) {
                    toast.error(t.errorPhone);
                    return;
                  }
                  setStep(s => s + 1);
                }} 
                className="flex-1 bg-teal-500 hover:bg-teal-600"
                disabled={(step === 1 && !formData.duration) || (step === 2 && (!formData.name || !formData.surname || !formData.email || !formData.phone))}
              >
                {t.next}
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
                disabled={isSubmitting || !hasSignature}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t.sending}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {t.submit}
                  </>
                )}
              </Button>
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* Regulations Modal */}
      <AnimatePresence>
        {showRegulations && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
            onClick={() => setShowRegulations(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-hidden shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-teal-500 to-cyan-600 p-4 text-white">
                <h3 className="text-lg font-bold">{regulationsContent[language]?.title || regulationsContent.it.title}</h3>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[60vh] space-y-4">
                {(regulationsContent[language]?.rules || regulationsContent.it.rules).map((rule) => (
                  <div key={rule.number} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-start gap-3">
                      <span className="bg-teal-500 text-white w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {rule.number}
                      </span>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {rule.text}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="p-4 border-t bg-gray-50">
                <Button
                  onClick={() => setShowRegulations(false)}
                  className="w-full bg-teal-500 hover:bg-teal-600"
                >
                  {regulationsContent[language]?.close || regulationsContent.it.close}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AnimatePresence>
  );
}
